import React, { createContext, useContext, useEffect, useState } from 'react';
import axios from 'axios';

const CurrencyContext = createContext({});
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Detecta moeda padrão pelo idioma/fuso do visitante (Brasil -> BRL, exterior -> EUR)
const detectDefaultCurrency = () => {
  try {
    const tz = (Intl.DateTimeFormat().resolvedOptions().timeZone || '').toLowerCase();
    const langs = (navigator.languages || [navigator.language || '']).join(',').toLowerCase();
    const brTimezones = ['sao_paulo', 'bahia', 'fortaleza', 'recife', 'manaus', 'belem', 'cuiaba', 'campo_grande', 'boa_vista', 'porto_velho', 'rio_branco', 'maceio', 'araguaina', 'santarem', 'noronha'];
    const isBrazil = brTimezones.some((z) => tz.includes(z)) || langs.includes('pt-br');
    return isBrazil ? 'BRL' : 'EUR';
  } catch {
    return 'BRL';
  }
};

const VALID = ['BRL', 'EUR', 'USD'];

export const CurrencyProvider = ({ children }) => {
  const savedInit = localStorage.getItem('currency');
  const [autoDetected] = useState(() => !VALID.includes(savedInit));
  const [currency, setCurrencyState] = useState(() => {
    if (VALID.includes(savedInit)) return savedInit;
    return detectDefaultCurrency();
  });
  const [eurRate, setEurRate] = useState(6.20);
  const [usdRate, setUsdRate] = useState(5.40);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/currency/config`)
      .then((res) => {
        if (res.data?.eur_rate) setEurRate(res.data.eur_rate);
        if (res.data?.usd_rate) setUsdRate(res.data.usd_rate);
      })
      .catch(() => {});
  }, []);

  const setCurrency = (c) => {
    setCurrencyState(c);
    localStorage.setItem('currency', c);
  };

  const convert = (brl) => {
    const n = Number(brl) || 0;
    if (currency === 'EUR') return n / eurRate;
    if (currency === 'USD') return n / usdRate;
    return n;
  };

  const fmt = (brl, { digits = 2 } = {}) => {
    const val = convert(brl);
    if (currency === 'EUR') {
      return `€ ${Math.round(val).toLocaleString('pt-PT', { maximumFractionDigits: 0 })}`;
    }
    if (currency === 'USD') {
      return `US$ ${Math.round(val).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
    }
    return `R$ ${val.toLocaleString('pt-BR', { minimumFractionDigits: digits, maximumFractionDigits: digits })}`;
  };

  return (
    <CurrencyContext.Provider value={{ currency, setCurrency, eurRate, usdRate, convert, fmt, autoDetected }}>
      {children}
    </CurrencyContext.Provider>
  );
};

export const useCurrency = () => {
  const ctx = useContext(CurrencyContext);
  if (!ctx || Object.keys(ctx).length === 0) {
    // Fallback seguro caso usado fora do provider
    return {
      currency: 'BRL',
      setCurrency: () => {},
      eurRate: 6.20,
      usdRate: 5.40,
      convert: (v) => Number(v) || 0,
      fmt: (v, o = {}) => `R$ ${(Number(v) || 0).toLocaleString('pt-BR', { minimumFractionDigits: o.digits ?? 2, maximumFractionDigits: o.digits ?? 2 })}`,
      autoDetected: false,
    };
  }
  return ctx;
};
